// The sabotage order on a spy's row (module `espionage.ops`).
//
// The tab asks and the sim decides: the cost, the cooldown and whether a
// frame is allowed all come from the snapshot (`ops`), so this carries no
// number of its own. A refusal (a weak network, a poor purse) comes back from
// the sim as `false`.

import type { PlayerHudEntry } from '@aod/ui';
import { useState } from 'react';
import type { EspionageOps, Spy, SpyTarget } from '../../espionage/espionage.js';
import { useT } from '../../i18n/index.js';
import { appDom } from '../../lib/appDom.js';
import type { SabotageOptions } from '../../sim/useSimWorld/useEspionageIntents.js';
import { FramePicker } from './FramePicker.js';

export interface SabotageControlsProps {
  readonly ops: EspionageOps;
  readonly spy: Spy;
  readonly target: SpyTarget;
  readonly players: readonly PlayerHudEntry[];
  readonly localPlayerIndex: number;
  readonly onSabotage: (unitId: string, cityId: string, opts?: SabotageOptions) => boolean;
}

export function SabotageControls({
  ops,
  spy,
  target,
  players,
  localPlayerIndex,
  onSabotage,
}: SabotageControlsProps) {
  const t = useT();
  const [frame, setFrame] = useState<number | null>(null);
  const [refused, setRefused] = useState(false);
  const busy = spy.mission !== null;
  const waiting = ops.cooldown > 0;
  // Blame anyone but yourself and the house you strike.
  const suspects = players.filter(
    (p) => p.playerId !== localPlayerIndex && p.playerId !== target.ownerId,
  );
  const id = `spy-sabotage-${spy.unitId}-${target.cityId}`; // for the two derived testids

  return (
    <div className="flex flex-col gap-1" data-testid={`${id}-controls`}>
      <div className="flex items-center gap-2">
        <FramePicker
          testId={`${id}-frame`}
          falseFlag={ops.falseFlag}
          suspects={suspects}
          value={frame}
          onChange={setFrame}
        />
        <button
          type="button"
          data-testid={`spy-sabotage-${spy.unitId}-${target.cityId}`}
          {...appDom({
            id: `spy-sabotage-${spy.unitId}-${target.cityId}`,
            type: 'Button',
            role: 'button',
            scope: 'section',
          })}
          disabled={busy || waiting}
          onClick={() => {
            const ok = onSabotage(
              spy.unitId,
              target.cityId,
              frame === null ? undefined : { frameSeat: frame },
            );
            setRefused(!ok);
          }}
          className="ml-auto shrink-0 rounded border border-border/60 px-2 py-0.5 text-[10px] hover:bg-muted/60 disabled:opacity-50"
        >
          {t('espionage.sabotage', { gold: ops.gold })}
        </button>
      </div>
      {busy && (
        <span className="text-[10px] text-muted-foreground">
          {t('espionage.sabotageUnderway', { n: spy.mission?.ticksLeft ?? 0 })}
        </span>
      )}
      {!busy && waiting && (
        <span className="text-[10px] text-muted-foreground">
          {t('espionage.sabotageCooldown', { n: ops.cooldown })}
        </span>
      )}
      {refused && (
        <output className="text-[10px] text-destructive">{t('espionage.sabotageRefused')}</output>
      )}
    </div>
  );
}
